
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { MessageSquare, UserPlus, UserCheck, Users, Heart, MessageCircle, Clock, Share2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { createFollowNotification } from "@/utils/notification-utils";
import FollowersDialog from "./dashboard/followers/FollowersDialog";
import FollowingDialog from "./dashboard/followers/FollowingDialog";
import UserProfilePost from "./social/UserProfilePost";
import UserProfilePostSkeleton from "./social/UserProfilePostSkeleton";

interface UserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  karma_points: number | null;
  interests: string | null;
  grade_level: string | null;
  country: string | null;
  state: string | null;
}

interface UserProfilePost {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  has_liked?: boolean;
  media?: {
    id: string;
    media_url: string;
    media_type: string;
  }[];
}

interface UserProfileDialogProps {
  userId: string | null;
  open: boolean;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ userId, open, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followers, setFollowers] = useState<any[]>([]);
  const [following, setFollowing] = useState<any[]>([]);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const [posts, setPosts] = useState<UserProfilePost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  useEffect(() => {
    if (open && userId && user && userId !== user.id) {
      loadProfile();
      checkFollowStatus();
      loadFollowStats();
      loadUserPosts();
    } else if (open && userId) {
      loadProfile();
      loadFollowStats();
      loadUserPosts();
    }
  }, [open, userId, user]);

  const loadProfile = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Error loading profile:", error);
      toast({
        title: "Error",
        description: "Failed to load user profile",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUserPosts = async () => {
    if (!userId) return;
    
    try {
      setLoadingPosts(true);
      
      // Fetch posts created by this user
      const { data: postsData, error: postsError } = await supabase
        .from('network_posts')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (postsError) throw postsError;
      
      // Get like counts
      const postsWithCounts = await Promise.all(postsData.map(async (post) => {
        // Get likes count
        const { count: likesCount, error: likesError } = await supabase
          .from('post_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', post.id)
          .eq('type', 'like');
          
        // Get comments count
        const { count: commentsCount, error: commentsError } = await supabase
          .from('post_interactions')
          .select('id', { count: 'exact', head: true })
          .eq('post_id', post.id)
          .eq('type', 'comment');
        
        // Check if current user has liked
        let hasLiked = false;
        if (user) {
          const { data: likeData } = await supabase
            .from('post_interactions')
            .select('id')
            .eq('post_id', post.id)
            .eq('user_id', user.id)
            .eq('type', 'like')
            .maybeSingle();
          
          hasLiked = !!likeData;
        }
        
        // Get post media
        const { data: mediaData } = await supabase
          .from('post_media')
          .select('*')
          .eq('post_id', post.id);
          
        return {
          ...post,
          likes_count: likesCount || 0,
          comments_count: commentsCount || 0,
          has_liked: hasLiked,
          media: mediaData || []
        };
      }));
      
      setPosts(postsWithCounts);
    } catch (error) {
      console.error("Error loading user posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleLikePost = async (postId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to like posts",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const post = posts.find(p => p.id === postId);
      const isLiked = post?.has_liked;
      
      // Optimistically update UI
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId
            ? { 
                ...post, 
                has_liked: !isLiked, 
                likes_count: isLiked ? post.likes_count - 1 : post.likes_count + 1 
              }
            : post
        )
      );
      
      if (isLiked) {
        // Remove like
        await supabase
          .from('post_interactions')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id)
          .eq('type', 'like');
      } else {
        // Add like
        await supabase
          .from('post_interactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            type: 'like'
          });
      }
    } catch (error) {
      console.error("Error liking/unliking post:", error);
      toast({
        title: "Error",
        description: "Failed to update like status",
        variant: "destructive"
      });
      // Revert optimistic update on error
      loadUserPosts();
    }
  };

  const handleComment = async (postId: string, commentText: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to comment on posts",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Add comment
      await supabase
        .from('post_interactions')
        .insert({
          post_id: postId,
          user_id: user.id,
          type: 'comment',
          comment_text: commentText
        });
      
      // Reload posts to get updated comment count
      loadUserPosts();
      
      toast({
        title: "Comment added",
        description: "Your comment was added successfully"
      });
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive"
      });
    }
  };

  const checkFollowStatus = async () => {
    if (!user || !userId) return;
    
    try {
      const { data } = await supabase
        .from('user_follows')
        .select('*')
        .eq('follower_id', user.id)
        .eq('following_id', userId)
        .single();
        
      setIsFollowing(!!data);
    } catch (error) {
      // Not following if no record found
      setIsFollowing(false);
    }
  };

  const loadFollowStats = async () => {
    if (!userId) return;
    
    try {
      const [followersRes, followingRes] = await Promise.all([
        supabase
          .from('user_follows')
          .select('*', { count: 'exact', head: true })
          .eq('following_id', userId),
        supabase
          .from('user_follows')
          .select('*', { count: 'exact', head: true })
          .eq('follower_id', userId)
      ]);
      
      setFollowersCount(followersRes.count || 0);
      setFollowingCount(followingRes.count || 0);
    } catch (error) {
      console.error("Error loading follow stats:", error);
    }
  };

  const handleFollow = async () => {
    if (!user || !userId) return;
    
    try {
      if (isFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', userId);
          
        setIsFollowing(false);
        setFollowersCount(prev => Math.max(0, prev - 1));
        toast({ title: "Unfollowed", description: "You are no longer following this user." });
      } else {
        await supabase
          .from('user_follows')
          .insert({ follower_id: user.id, following_id: userId });
          
        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        
        // Create notification
        const { data: currentUserProfile } = await supabase
          .from('profiles')
          .select('full_name, username')
          .eq('id', user.id)
          .single();
          
        const followerName = currentUserProfile?.full_name || currentUserProfile?.username || 'Someone';
        await createFollowNotification(userId, followerName);
        
        toast({ title: "Following", description: "You are now following this user." });
      }
    } catch (error) {
      console.error("Error updating follow status:", error);
      toast({ title: "Error", description: "Could not update follow status.", variant: "destructive" });
    }
  };

  const handleMessage = () => {
    window.location.href = `/messages/${userId}`;
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };

  if (!profile && !loading) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">User Profile</DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : profile ? (
            <div className="space-y-4">
              {/* Profile Header */}
              <div className="flex flex-col md:flex-row items-center gap-4">
                <Avatar className="h-20 w-20 md:h-24 md:w-24">
                  <AvatarImage src={profile.avatar_url || undefined} />
                  <AvatarFallback>{getInitials(profile.full_name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="font-semibold text-xl">{profile.full_name || 'Anonymous User'}</h3>
                  <p className="text-sm text-muted-foreground">@{profile.username || 'username'}</p>
                  <div className="flex items-center justify-center md:justify-start mt-2 gap-2">
                    {(profile.karma_points || 0) >= 100 && (
                      <Badge variant="secondary" className="mt-1">
                        {profile.karma_points} karma
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant={isFollowing ? "secondary" : "default"} 
                    onClick={handleFollow}
                  >
                    {isFollowing ? (
                      <>
                        <UserCheck className="h-4 w-4 mr-1" />
                        Following
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-1" />
                        Follow
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleMessage}>
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Message
                  </Button>
                </div>
              </div>

              {/* Follow Stats */}
              <div className="flex justify-center md:justify-start gap-4 bg-muted/30 rounded-md p-2">
                <button 
                  onClick={() => setShowFollowers(true)}
                  className="text-center hover:bg-accent rounded p-2 transition-colors"
                >
                  <div className="font-semibold">{followersCount}</div>
                  <div className="text-sm text-muted-foreground">Followers</div>
                </button>
                <button 
                  onClick={() => setShowFollowing(true)}
                  className="text-center hover:bg-accent rounded p-2 transition-colors"
                >
                  <div className="font-semibold">{followingCount}</div>
                  <div className="text-sm text-muted-foreground">Following</div>
                </button>
              </div>

              <Separator />

              {/* Tabbed Content */}
              <Tabs defaultValue="posts" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="posts" className="flex-1">Posts</TabsTrigger>
                  <TabsTrigger value="about" className="flex-1">About</TabsTrigger>
                </TabsList>
                
                <TabsContent value="posts" className="space-y-4 mt-4">
                  {loadingPosts ? (
                    <div className="space-y-6">
                      <UserProfilePostSkeleton />
                      <UserProfilePostSkeleton />
                    </div>
                  ) : posts.length > 0 ? (
                    <div className="space-y-6">
                      {posts.map((post) => (
                        <UserProfilePost
                          key={post.id}
                          post={post}
                          profile={profile}
                          onLike={() => handleLikePost(post.id)}
                          onComment={(comment) => handleComment(post.id, comment)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-muted/20 rounded-lg border border-dashed">
                      <p className="text-muted-foreground">No posts yet</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="about" className="space-y-4 mt-4">
                  {/* Bio */}
                  {profile.bio && (
                    <div>
                      <h4 className="font-medium mb-2">Bio</h4>
                      <p className="text-sm text-muted-foreground">{profile.bio}</p>
                    </div>
                  )}

                  {/* Interests */}
                  {profile.interests && (
                    <div>
                      <h4 className="font-medium mb-2">Interests</h4>
                      <p className="text-sm text-muted-foreground">{profile.interests}</p>
                    </div>
                  )}

                  {/* Location & Grade */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {profile.country && (
                      <div>
                        <span className="font-medium">Location:</span>
                        <p className="text-muted-foreground">
                          {profile.state ? `${profile.state}, ${profile.country}` : profile.country}
                        </p>
                      </div>
                    )}
                    {profile.grade_level && (
                      <div>
                        <span className="font-medium">Grade:</span>
                        <p className="text-muted-foreground">{profile.grade_level}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <FollowersDialog 
        open={showFollowers} 
        onClose={() => setShowFollowers(false)} 
        followers={followers}
        loading={false}
      />

      <FollowingDialog 
        open={showFollowing} 
        onClose={() => setShowFollowing(false)} 
        following={following}
        loading={false}
      />
    </>
  );
};

export default UserProfileDialog;
